<template>
  <div class="request-shell">
    <section class="card-section">
      <h2 class="section-title">Solicitar item do almoxarife</h2>
      <form class="form-grid" @submit.prevent="createRequest">
        <div class="grid request-grid">
          <div class="input-field">
            <label for="req-item">Item</label>
            <select id="req-item" v-model="form.itemId">
              <option value="">Selecione</option>
              <option v-for="item in items" :key="item.id" :value="item.id">
                {{ item.name }} (disp: {{ item.availableQuantity }})
              </option>
            </select>
          </div>
          <div class="input-field">
            <label for="req-qty">Quantidade</label>
            <input id="req-qty" v-model.number="form.quantity" type="number" min="1" />
          </div>
          <div class="input-field">
            <label for="req-notes">Observacao</label>
            <input id="req-notes" v-model="form.notes" type="text" placeholder="Opcional" />
          </div>
        </div>
        <div class="actions">
          <button class="btn btn-primary" type="submit">Solicitar item</button>
        </div>
      </form>
    </section>

    <section class="card-section">
      <div class="section-header">
        <h2 class="section-title">Itens que estao sobre sua custodia</h2>
        <button class="btn btn-ghost" type="button" @click="toggleReturned">
          {{ showReturned ? 'Ocultar ferramentas devolvidas' : 'Consultar ferramentas ja devolvidas' }}
        </button>
      </div>
      <div v-if="requests.length === 0" class="hint">Nenhum item para exibicao.</div>
      <div v-else class="list">
        <article v-for="entry in requests" :key="entry.id" class="item-card request-card">
          <div>
            <strong>{{ itemLabel(entry.itemId) }}</strong>
            <div class="item-meta">Quantidade: {{ entry.quantity }}</div>
            <div class="item-meta">Status: {{ statusLabel(entry.status) }}</div>
            <div class="item-meta">Atualizado em: {{ entry.updatedAt || entry.createdAt }}</div>
          </div>
          <div class="actions">
            <button
              v-if="entry.status === 2"
              class="btn btn-primary"
              type="button"
              @click="confirmProvided(entry.id, true)"
            >
              Foi fornecido
            </button>
            <button
              v-if="entry.status === 2"
              class="btn btn-ghost"
              type="button"
              @click="confirmProvided(entry.id, false)"
            >
              Nao fornecido
            </button>
            <button
              v-if="entry.status === 3"
              class="btn btn-ghost"
              type="button"
              @click="requestReturn(entry.id)"
            >
              Solicitar devolucao
            </button>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { apiFetch } from '@/store/api'
import { warehouseStore } from '@/store/warehouse'

interface WarehouseUserRequest {
  id: string
  itemId: string
  quantity: number
  status: number
  notes: string
  createdAt: string
  updatedAt: string
}

export default defineComponent({
  name: 'WarehouseRequestItemView',
  setup() {
    const showReturned = ref(false)
    const requests = ref<WarehouseUserRequest[]>([])
    const form = ref({
      itemId: '',
      quantity: 1,
      notes: ''
    })

    const items = computed(() => warehouseStore.all())

    const statusLabel = (status: number) => {
      if (status === 0) return 'aguardando aprovacao'
      if (status === 1) return 'aprovado'
      if (status === 2) return 'aguardando confirmacao de fornecimento'
      if (status === 3) return 'fornecido'
      if (status === 4) return 'aguardando devolucao'
      if (status === 5) return 'devolvido'
      return `status-${status}`
    }

    const itemLabel = (itemId: string) => {
      const item = items.value.find((entry) => entry.id === itemId)
      return item ? `${item.name} (${item.code})` : itemId
    }

    const refreshRequests = async () => {
      const includeReturned = showReturned.value ? '1' : '0'
      requests.value = await apiFetch<WarehouseUserRequest[]>(`/warehouse/user-requests?mine=1&includeReturned=${includeReturned}`)
    }

    const createRequest = async () => {
      if (!form.value.itemId || form.value.quantity < 1) return
      await apiFetch('/warehouse/user-requests', {
        method: 'POST',
        body: JSON.stringify({
          itemId: form.value.itemId,
          quantity: form.value.quantity,
          notes: form.value.notes
        })
      })
      form.value = { itemId: '', quantity: 1, notes: '' }
      await refreshRequests()
    }

    const requestReturn = async (id: string) => {
      await apiFetch(`/warehouse/user-requests/${encodeURIComponent(id)}/request-return`, {
        method: 'POST',
        body: JSON.stringify({})
      })
      await refreshRequests()
    }

    const confirmProvided = async (id: string, provided: boolean) => {
      await apiFetch(`/warehouse/user-requests/${encodeURIComponent(id)}/confirm-provided`, {
        method: 'POST',
        body: JSON.stringify({ provided })
      })
      await refreshRequests()
    }

    const toggleReturned = async () => {
      showReturned.value = !showReturned.value
      await refreshRequests()
    }

    onMounted(() => {
      Promise.all([warehouseStore.refresh(), refreshRequests()]).catch(() => {
        // ignore bootstrap error
      })
    })

    return {
      form,
      items,
      requests,
      showReturned,
      statusLabel,
      itemLabel,
      createRequest,
      requestReturn,
      confirmProvided,
      toggleReturned
    }
  }
})
</script>

<style scoped lang="scss">
.request-shell {
  display: grid;
  gap: 16px;
}

.card-section {
  border: 1px solid rgba(31, 30, 26, 0.1);
  border-radius: 14px;
  background: #fff;
  padding: 14px;
}

.request-grid {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.request-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
